import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button, Card, CardBody, CardHeader, CardTitle } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faRobot, faPaperPlane, faRotateLeft, faCircleQuestion, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { requestPromptLabClaude } from "../../services/RobotAIServices.js";
import ListenButton from "../LessonAudio/ListenButton";
import PaneResizer, { clampSize } from "./PaneResizer.js";
import { LiveStatus, useStatus, useOnChange, refocusIfLost } from "./workspaceA11y.js";
import { buildHotspotSrcDoc } from "./hotspotHarness.js";
import { buildAuthoredHotspots } from "../../starterTemplate/authoredLessons.js";
import "./PromptLabLayout.css";
import "./EditorLayout.css";
import "./HotspotLayout.css";
import { AiFirstUseNotice, AiMistakesNote } from "../AiNotice/AiNotice";

/**
 * CD02 "hotspot" layout — Module 6's click-to-identify screens:
 *
 *   +----------+‖-----------------------------+
 *   |  Lesson  |‖  Prompt strip  (n of m)     |
 *   |          |‖+===========================+|
 *   |          |‖  Replica of the real app    ||
 *   |          |‖  (click the right part)     ||
 *   |          |‖+===========================+|
 *   |          |‖  + Ask Claude               ||
 *   +----------+‖-----------------------------+
 *
 * Module 6 teaches how to OPERATE the Claude Code desktop app. Its replicas were
 * always right, but the exercise on top of them used to be fill-in-the-blank HTML:
 * the student practiced "text goes between the tags" — a Module 3 objective — to
 * learn a menu, and the answers were printed on screen beside every row. Here the
 * screen arrives FINISHED and the student answers questions by clicking it.
 *
 * The questions are phrased as situations, never as labels ("a big refactor and you
 * want the strongest reasoning", not "click the most capable model"), so the answer
 * cannot be read off the page. That rule is what makes this an exercise instead of
 * a transcription task; it is enforced by review, not by code, and is written up
 * where the questions are authored (authoredLessons.js).
 *
 * Two panes, not Web Lab's three: there is no editor, no Run and no download,
 * because there is nothing for the student to write. Grading is entirely
 * client-side — every target found calls onComplete once.
 */

// One target at a time, in authored order. Everything about scoring lives here in
// the parent; the frame only paints what it is told to (see hotspotHarness.js).
// `screen` arrives as a prop rather than being built here: the replica carries the
// student's own app name, which lives in `users_projects`, and the hub is what loads
// that record (exactly as it does for the Web Lab scaffolds).
const HotspotLayout = ({ definition, lessonTitle, screen, onComplete, allPassed, askHelpSignal, solveSignal }) => {
    const { t } = useTranslation();
    const [leftWidth, setLeftWidth] = useState(380);
    const containerRef = useRef(null);
    const iframeRef = useRef(null);

    const lessonID = definition?.lessonID;

    // Taken from the definition, NOT a live i18n read — same reasoning as
    // WebLabLayout: the hub's init effect only re-runs on route change, so reading
    // i18n live would flip the prompts out from under the student mid-lesson. The
    // hub's freshness gate already guarantees def.locale matches the UI language.
    const locale = definition?.locale || "en";

    const hotspots = useMemo(() => buildAuthoredHotspots(lessonID, locale), [lessonID, locale]);
    const targets = useMemo(() => hotspots?.targets || [], [hotspots]);

    // Ids already answered, in the order they were found.
    const [found, setFound] = useState([]);
    const [nudge, setNudge] = useState(null);
    const [frameError, setFrameError] = useState(null);

    // Identifies the current mount of the frame. A message from a previous srcDoc
    // (after Start over reloads it) carries a stale nonce and is ignored, so a late
    // click cannot land on the new run.
    const [nonce, setNonce] = useState(() => `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const nonceRef = useRef(nonce);
    nonceRef.current = nonce;

    // The click handler has to know what has already been found, but must not be
    // rebuilt every time that changes: the frame posts into whichever listener is
    // installed, and tearing one down between a click and its message would drop
    // the answer. Mirror the state into a ref and keep the listener stable.
    const foundRef = useRef(found);
    foundRef.current = found;

    // Ask Claude, mirroring WebLabLayout's slim helper.
    const [askOpen, setAskOpen] = useState(false);
    const [askInput, setAskInput] = useState("");
    const [askMessages, setAskMessages] = useState([]);
    const [asking, setAsking] = useState(false);
    const askEndRef = useRef(null);
    const askInputRef = useRef(null);
    const sendHintId = useId();

    const remaining = useMemo(
        () => targets.filter((tg) => !found.includes(tg.id)),
        [targets, found]
    );
    const current = remaining[0] || null;
    const complete = targets.length > 0 && remaining.length === 0;

    const srcDoc = useMemo(
        () => (screen ? buildHotspotSrcDoc(screen, hotspots || {}, nonce) : null),
        [screen, hotspots, nonce]
    );

    // Initial split — lesson ~30%, the replica takes the rest. It is 720px wide at
    // most and is the thing being read, so it gets more room than Web Lab's editor.
    useEffect(() => {
        const setInitial = () => {
            const total = containerRef.current ? containerRef.current.clientWidth : window.innerWidth;
            setLeftWidth(Math.min(460, Math.max(280, Math.floor(total * 0.3))));
        };
        setInitial();
        window.addEventListener("resize", setInitial);
        return () => window.removeEventListener("resize", setInitial);
    }, []);

    useEffect(() => {
        if (askEndRef.current) askEndRef.current.scrollIntoView({ behavior: "smooth" });
    }, [askMessages.length, asking]);

    const mark = useCallback((message) => {
        const frame = iframeRef.current;
        if (!frame || !frame.contentWindow) return;
        // Only an id, a state word and authored label copy cross the boundary.
        frame.contentWindow.postMessage({ ...message, nonce: nonceRef.current }, "*");
    }, []);

    // Report completion once, and only on a real transition — `complete` is derived,
    // so without the guard every later render would call it again.
    const reportedRef = useRef(false);
    useEffect(() => {
        if (complete && !reportedRef.current) {
            reportedRef.current = true;
            // Uncover the descriptions of the rows that were never the answer. Those
            // distractor rows are what make a wrong click teach something, but they
            // never get "found", so on a revealDesc screen their text would stay
            // blurred and half the menu would be unreadable at the end.
            if (hotspots?.revealDesc) mark({ type: "hotspot:mark", state: "reveal" });
            onComplete?.();
        }
    }, [complete, onComplete, hotspots, mark]);

    // ----------------------------------------------------------------------
    // Clicks from the replica.
    //
    // The frame is sandboxed WITHOUT allow-same-origin, so its origin is "null"
    // and event.origin can't authenticate anything. Match on the frame's own
    // window plus this mount's nonce, exactly as WebLabLayout does.
    // ----------------------------------------------------------------------
    useEffect(() => {
        const onMessage = (event) => {
            const frame = iframeRef.current;
            if (!frame || event.source !== frame.contentWindow) return;

            const data = event.data;
            if (!data || typeof data !== "object") return;
            if (!nonceRef.current || data.nonce !== nonceRef.current) return;

            if (data.type === "hotspot:ready") {
                // A question with no answer on screen is unwinnable and the student
                // would have no way to know. The authoring tests catch this pair
                // before it ships; this catches it at runtime and says so out loud.
                const present = new Set(Array.isArray(data.ids) ? data.ids : []);
                const missing = targets.map((tg) => tg.id).filter((id) => !present.has(id));
                if (missing.length > 0) {
                    console.warn(`[hotspot] ${lessonID}: no [data-hs] on screen for ${missing.join(", ")}`);
                }
                return;
            }

            if (data.type === "hotspot:error") {
                setFrameError(String(data.message || ""));
                return;
            }

            if (data.type !== "hotspot:hit") return;

            // Dead space. Say so rather than ignoring it — a click that does nothing
            // reads as a broken page.
            if (!data.id) {
                setNudge({ kind: "empty", text: t("hotspot.nothingThere") });
                return;
            }

            // Read through the ref, and do the marking OUT here: a setState updater
            // has to stay pure, and StrictMode double-invokes it — posting into the
            // frame from inside one would fire every mark twice.
            const already = foundRef.current;
            if (already.includes(data.id)) return;

            const target = targets.find((tg) => !already.includes(tg.id));
            if (!target) return;

            if (data.id === target.id) {
                mark({ type: "hotspot:mark", id: target.id, state: "found", label: target.label });
                setNudge(null);
                setFound([...already, target.id]);
                return;
            }

            // Wrong, but never blocking and never scored down. `miss` is optional per
            // target — most confusions are not worth their own sentence.
            mark({ type: "hotspot:mark", id: data.id, state: "miss" });
            setNudge({ kind: "miss", text: target.miss || t("hotspot.notQuite") });
        };

        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [targets, mark, lessonID, t]);

    // Remounts the frame with a fresh nonce, which clears every mark with it.
    const handleStartOver = useCallback(() => {
        setFound([]);
        setNudge(null);
        setFrameError(null);
        reportedRef.current = false;
        setNonce(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
    }, []);

    // The divider's limits for a given width, shared by dragging and the keyboard.
    const leftLimits = (width) => ({ min: 260, max: width - 12 - 320 });

    // Drag: lesson ↔ replica.
    const startLeftResize = (e) => {
        e.preventDefault();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const onMove = (ev) => {
            const { min, max } = leftLimits(rect.width);
            setLeftWidth(clampSize(ev.clientX - rect.left, min, max));
        };
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            document.body.classList.remove("is-resizing-col");
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        document.body.classList.add("is-resizing-col");
    };

    // `overrideQuestion` comes from the navbar's "Stuck?" panel, which asks on the
    // student's behalf instead of through the composer.
    const handleAsk = async (overrideQuestion) => {
        const question = (typeof overrideQuestion === "string" ? overrideQuestion : askInput).trim();
        if (!question || asking) return;

        // Seed Claude with the question the student is stuck on and what they have
        // already identified — the equivalent of Web Lab passing the failing checks.
        // The exercise goes in too, so Claude cannot contradict the instructions
        // being read beside it. The screen's HTML deliberately does NOT: this lesson
        // is about the real app, and pasting the replica's markup invites Claude to
        // talk about the mock instead of the tool.
        const contextual =
            (definition?.exercisePrompt ? `The exercise I was given:\n${definition.exercisePrompt}\n\n` : "") +
            `I am looking at a replica of the Claude Code desktop app and identifying parts of it by clicking them.\n\n` +
            (current ? `The question I am on: ${current.prompt}\n\n` : "") +
            (found.length > 0
                ? `Already identified: ${found
                      .map((id) => targets.find((tg) => tg.id === id)?.label || id)
                      .join(", ")}\n\n`
                : "") +
            `My question: ${question}`;

        const sendMessages = [...askMessages, { role: "user", content: contextual }];
        setAskMessages((prev) => [...prev, { role: "user", content: question }]);
        if (typeof overrideQuestion !== "string") setAskInput("");
        setAsking(true);

        try {
            const sys = definition?.systemPromptForClaude
                || "You are a friendly tutor helping a beginner learn the Claude Code desktop app. Explain what each part of the window is for, in plain language, and nudge them toward the right one — never just name the answer.";
            const result = await requestPromptLabClaude(sys, sendMessages, definition?.lessonID);
            setAskMessages((prev) => [...prev, { role: "assistant", content: result?.text || t("promptLab.noResponse") }]);
        } catch (e) {
            setAskMessages((prev) => [...prev, { role: "assistant", content: t("promptLab.askCouldNotReach") }]);
        } finally {
            setAsking(false);
        }
    };

    const handleAskKeyDown = (e) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleAsk();
        }
    };

    // Send keeps focus in the box; the button disables itself once the box is
    // empty, which would otherwise drop focus to <body>.
    const askFromButton = () => {
        handleAsk();
        askInputRef.current?.focus();
    };

    // Spoken summaries: a reply is coming or has come.
    const [status, announce] = useStatus();

    useOnChange(asking, (busy) => {
        if (busy) return announce(t("promptLab.statusReplying"));
        announce(t("promptLab.statusReplied"));
        refocusIfLost(askInputRef.current);
    });

    // Driven by the navbar's "Stuck?" panel. The helper is collapsed by default, so
    // open it first — otherwise Claude's answer lands out of sight.
    useEffect(() => {
        if (!askHelpSignal) return;
        setAskOpen(true);
        handleAsk(t("promptLab.hintAskMessage"));
    }, [askHelpSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    // "Do it for me": there is nothing to generate here — the answers are authored —
    // so it just marks every remaining target found. The replica gets the same
    // per-target message a real click sends, so the labels land on the screen the way
    // the student would have earned them, and `complete` then reports upward.
    useEffect(() => {
        if (!solveSignal) return;
        const already = foundRef.current;
        targets.forEach((tg) => {
            if (!already.includes(tg.id)) {
                mark({ type: "hotspot:mark", id: tg.id, state: "found", label: tg.label });
            }
        });
        setNudge(null);
        setFound(targets.map((tg) => tg.id));
    }, [solveSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    const dragTitle = t("promptLab.dragToResize", "Drag to resize");
    const exercisePassed = allPassed || complete;

    return (
        <div ref={containerRef} className="prompt-lab-root">
            {/* LEFT: Lesson */}
            <div className="prompt-lab-pane prompt-lab-pane--lesson" style={{ width: leftWidth, flex: "0 0 auto" }}>
                <div className="prompt-lab-pane-inner">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <CardTitle tag="h5" className="mb-0">{lessonTitle || definition?.title || t("promptLab.lessonFallback")}</CardTitle>
                            <ListenButton text={definition?.lessonMarkdown} lessonID={definition?.lessonID} />
                        </CardHeader>
                        <CardBody className="prompt-lab-lesson-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {definition?.lessonMarkdown || ""}
                            </ReactMarkdown>

                            {definition?.exercisePrompt && (
                                <div className={`prompt-lab-exercise${exercisePassed ? " prompt-lab-exercise--passed" : ""}`}>
                                    <div className="prompt-lab-exercise-head">
                                        <strong>{t("promptLab.yourExercise")}</strong>
                                        {exercisePassed && (
                                            <span className="prompt-lab-exercise-badge passed">
                                                <FontAwesomeIcon icon={faCheck} /> {t("promptLab.passed")}
                                            </span>
                                        )}
                                    </div>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {definition.exercisePrompt}
                                    </ReactMarkdown>
                                </div>
                            )}

                        </CardBody>
                    </Card>
                </div>
            </div>

            <PaneResizer
                value={leftWidth}
                {...leftLimits(containerRef.current?.clientWidth || window.innerWidth)}
                onResize={setLeftWidth}
                onPointerDown={startLeftResize}
                label={t("promptLab.resizePane", { pane: t("promptLab.lessonFallback") })}
                title={dragTitle}
            />

            {/* RIGHT: prompt strip + replica + Ask Claude */}
            <div className="prompt-lab-pane prompt-lab-pane--right" style={{ flex: "1 1 auto" }}>
                <div className="hotspot-screen">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <span>{t("hotspot.screenTitle")}</span>
                            <span className="hotspot-count">
                                {targets.length > 0 && t("hotspot.foundOf", { found: found.length, total: targets.length })}
                                {found.length > 0 && (
                                    <button type="button" className="hotspot-restart" onClick={handleStartOver} title={t("hotspot.startOver")}>
                                        <FontAwesomeIcon icon={faRotateLeft} /> {t("hotspot.startOver")}
                                    </button>
                                )}
                            </span>
                        </CardHeader>

                        {/* The question. aria-live so the prompt and the nudge are
                            announced as they change — the whole exercise happens
                            here, and a screen reader user would otherwise have to
                            hunt for what moved. */}
                        <div className="hotspot-prompt" aria-live="polite">
                            {complete ? (
                                <div className="hotspot-prompt-done">
                                    <FontAwesomeIcon icon={faCheck} /> {t("hotspot.allFound")}
                                </div>
                            ) : current ? (
                                <>
                                    <div className="hotspot-prompt-step">
                                        {t("hotspot.stepOf", { step: found.length + 1, total: targets.length })}
                                    </div>
                                    <div className="hotspot-prompt-text">
                                        <FontAwesomeIcon icon={faCircleQuestion} /> {current.prompt}
                                    </div>
                                </>
                            ) : (
                                <div className="hotspot-prompt-text">{t("hotspot.noQuestions")}</div>
                            )}

                            {nudge && !complete && (
                                <div className={`hotspot-nudge hotspot-nudge--${nudge.kind}`}>{nudge.text}</div>
                            )}
                        </div>

                        <CardBody className="hotspot-screen-body">
                            {frameError && (
                                <div className="hotspot-frame-error">
                                    <FontAwesomeIcon icon={faTriangleExclamation} /> {frameError}
                                </div>
                            )}
                            {srcDoc ? (
                                <iframe
                                    key={nonce}
                                    ref={iframeRef}
                                    className="hotspot-frame"
                                    title={t("hotspot.screenTitle")}
                                    srcDoc={srcDoc}
                                    // NEVER add allow-same-origin here. V2605's replica
                                    // interpolates {{APP_NAME}} — text the student typed —
                                    // and combined with allow-scripts this flag dissolves
                                    // the sandbox and lets page code reach the parent's
                                    // localStorage, where Firebase keeps auth tokens.
                                    // See hotspotHarness.js.
                                    sandbox="allow-scripts"
                                />
                            ) : (
                                <div className="hotspot-empty">{t("hotspot.noScreen")}</div>
                            )}
                        </CardBody>
                    </Card>
                </div>

                {/* Slim "Ask Claude" hook — same pattern as WebLabLayout */}
                <div className="editor-ask-claude">
                    {askOpen && (
                        <div className="editor-ask-claude-thread">
                            {/* A log, so new messages are read out; the status line
                                covers "Thinking…". */}
                            <div className="editor-ask-claude-log" role="log" aria-live="polite" aria-relevant="additions">
                                {askMessages.map((m, i) => (
                                    <div key={i} className={`prompt-lab-msg prompt-lab-msg--${m.role}`}>
                                        <div className="prompt-lab-msg-role">{m.role === "user" ? t("promptLab.you") : "Claude"}</div>
                                        <div className="prompt-lab-msg-content">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || ""}</ReactMarkdown>
                                        </div>
                                    </div>
                                ))}
                                {asking && (
                                    <div className="prompt-lab-msg prompt-lab-msg--assistant prompt-lab-msg--thinking" aria-hidden="true">
                                        <div className="prompt-lab-msg-content">{t("promptLab.thinking")}</div>
                                    </div>
                                )}
                                <div ref={askEndRef} />
                            </div>
                            <AiFirstUseNotice />
                            <div className="editor-ask-claude-input">
                                {/* Read-only, not disabled, while Claude replies, so
                                    keyboard focus stays here. */}
                                <textarea
                                    ref={askInputRef}
                                    value={askInput}
                                    onChange={(e) => setAskInput(e.target.value)}
                                    onKeyDown={handleAskKeyDown}
                                    placeholder={t("hotspot.askPlaceholder")}
                                    rows={2}
                                    aria-label={t("promptLab.messageLabel")}
                                    aria-describedby={sendHintId}
                                    readOnly={asking}
                                    aria-busy={asking}
                                />
                                <Button
                                    size="sm"
                                    color="primary"
                                    onClick={askFromButton}
                                    disabled={asking || !askInput.trim()}
                                    aria-label={t("promptLab.send")}
                                    title={t("promptLab.send")}
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} />
                                </Button>
                            </div>
                            <div id={sendHintId} className="pl-chat-hint">{t("promptLab.sendShortcutHint")}</div>
                            <AiMistakesNote />
                        </div>
                    )}
                    <button type="button" className="editor-ask-claude-toggle" onClick={() => setAskOpen((o) => !o)} aria-expanded={askOpen}>
                        <FontAwesomeIcon icon={faRobot} /> &nbsp;
                        {askOpen ? t("promptLab.hideClaude") : t("promptLab.askClaudeStuck")}
                    </button>
                </div>
            </div>

            <LiveStatus message={status} />
        </div>
    );
};

export default HotspotLayout;
